/* ============================================================
   MYC PERFORMANCE — social proof video carousel
   Center-snap rail of portrait videos. Videos lazy-load and
   autoplay muted while on screen; unmuting one card mutes the
   rest. Arrows, drag-scroll and keyboard all drive the same
   native scroll position, so snap stays in charge.
   ============================================================ */
(function () {
  'use strict';

  var REDUCE = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initCarousel(root) {
    if (root.dataset.vcarReady) return;
    root.dataset.vcarReady = '1';

    var rail = root.querySelector('[data-vcar-rail]');
    if (!rail) return;
    var cards = Array.prototype.slice.call(rail.querySelectorAll('[data-vcar-card]'));
    if (!cards.length) return;

    /* ---------- lazy video mount ---------- */
    function mountVideo(card) {
      if (card.dataset.mounted) return card.querySelector('video');
      card.dataset.mounted = '1';
      var mp4 = card.getAttribute('data-mp4');
      var webm = card.getAttribute('data-webm');
      if (!mp4 && !webm) return null;
      var video = document.createElement('video');
      video.muted = true;
      video.loop = true;
      video.playsInline = true;
      video.setAttribute('playsinline', '');
      video.setAttribute('muted', '');
      video.preload = 'auto';
      video.className = 'myc-vcar__video';
      var poster = card.getAttribute('data-poster');
      if (poster) video.poster = poster;
      if (webm) {
        var sw = document.createElement('source');
        sw.src = webm; sw.type = 'video/webm';
        video.appendChild(sw);
      }
      if (mp4) {
        var sm = document.createElement('source');
        sm.src = mp4; sm.type = 'video/mp4';
        video.appendChild(sm);
      }
      var stage = card.querySelector('[data-vcar-stage]');
      if (stage) stage.appendChild(video);
      video.addEventListener('playing', function () { card.classList.add('is-playing'); });
      video.addEventListener('pause', function () { card.classList.remove('is-playing'); });
      return video;
    }

    function tryPlay(card) {
      if (REDUCE || card.dataset.userPaused) return;
      var video = mountVideo(card);
      if (!video) return;
      var p = video.play();
      if (p && p.catch) p.catch(function () {});
    }

    function pauseCard(card) {
      var video = card.querySelector('video');
      if (video && !video.paused) video.pause();
    }

    /* ---------- in-view autoplay ---------- */
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) {
        if (en.isIntersecting) tryPlay(en.target);
        else pauseCard(en.target);
      });
    }, { root: null, rootMargin: '120px 0px', threshold: 0.45 });
    cards.forEach(function (card) { io.observe(card); });

    /* ---------- center emphasis ---------- */
    var scaleRaf = null;
    function paintScales() {
      scaleRaf = null;
      var railRect = rail.getBoundingClientRect();
      var center = railRect.left + railRect.width / 2;
      cards.forEach(function (card) {
        var r = card.getBoundingClientRect();
        var d = Math.abs(r.left + r.width / 2 - center);
        var t = Math.min(1, d / (railRect.width * 0.55));
        card.style.setProperty('--vc-t', t.toFixed(3));
        card.classList.toggle('is-center', d < r.width * 0.5);
      });
    }
    function queueScales() {
      if (scaleRaf) return;
      scaleRaf = requestAnimationFrame(paintScales);
    }
    rail.addEventListener('scroll', queueScales, { passive: true });
    window.addEventListener('resize', queueScales, { passive: true });

    /* Open on the middle card so both gutters are populated — starting on
       card 1 leaves a dead half-viewport to its left.

       This has to be re-applied rather than set once: the rail is
       `scroll-snap-type: x mandatory`, and every time lazily-mounted media
       changes the layout the browser re-snaps to the nearest snap point,
       which on a cold load throws the rail back to card 1. So we re-centre
       on the next frame, on window load, and twice more as media settles —
       but never once the visitor has touched the rail. offsetLeft/offsetWidth
       are used instead of getBoundingClientRect because they don't depend on
       the current scroll position. */
    var userTouched = false;
    ['pointerdown', 'wheel', 'touchstart', 'keydown'].forEach(function (ev) {
      rail.addEventListener(ev, function () { userTouched = true; }, { passive: true });
    });

    function centreOnMiddleCard() {
      if (userTouched) return;
      var mid = cards[Math.floor((cards.length - 1) / 2)];
      if (!mid || !rail.clientWidth) return;
      var target = mid.offsetLeft - (rail.clientWidth - mid.offsetWidth) / 2;
      if (target < 0) target = 0;
      if (Math.abs(rail.scrollLeft - target) > 2) {
        rail.scrollLeft = target;
        queueScales();
      }
    }

    centreOnMiddleCard();
    requestAnimationFrame(centreOnMiddleCard);
    window.addEventListener('load', centreOnMiddleCard);
    setTimeout(centreOnMiddleCard, 400);
    setTimeout(centreOnMiddleCard, 1200);
    paintScales();

    /* ---------- arrows ---------- */
    function step() {
      var card = cards[0];
      var gap = parseFloat(getComputedStyle(rail).columnGap || getComputedStyle(rail).gap) || 16;
      return card.getBoundingClientRect().width + gap;
    }
    root.querySelectorAll('[data-vcar-prev]').forEach(function (btn) {
      btn.addEventListener('click', function () { rail.scrollBy({ left: -step(), behavior: 'smooth' }); });
    });
    root.querySelectorAll('[data-vcar-next]').forEach(function (btn) {
      btn.addEventListener('click', function () { rail.scrollBy({ left: step(), behavior: 'smooth' }); });
    });

    /* ---------- per-card controls ---------- */
    root.addEventListener('click', function (e) {
      var t;
      if ((t = e.target.closest('[data-vcar-mute]'))) {
        var card = t.closest('[data-vcar-card]');
        var video = mountVideo(card);
        if (!video) return;
        var unmuting = video.muted;
        if (unmuting) {
          /* only one card gets sound */
          cards.forEach(function (other) {
            var ov = other.querySelector('video');
            if (ov && other !== card) {
              ov.muted = true;
              other.classList.remove('is-sound');
            }
          });
        }
        video.muted = !unmuting;
        card.classList.toggle('is-sound', unmuting);
        t.setAttribute('aria-pressed', unmuting ? 'true' : 'false');
        if (unmuting) {
          delete card.dataset.userPaused;
          tryPlay(card);
        }
        return;
      }
      if ((t = e.target.closest('[data-vcar-toggle]'))) {
        var card2 = t.closest('[data-vcar-card]');
        var video2 = mountVideo(card2);
        if (!video2) return;
        if (video2.paused) {
          delete card2.dataset.userPaused;
          var p = video2.play();
          if (p && p.catch) p.catch(function () {});
          t.setAttribute('aria-pressed', 'false');
        } else {
          card2.dataset.userPaused = '1';
          video2.pause();
          t.setAttribute('aria-pressed', 'true');
        }
      }
    });

    /* ---------- desktop drag-scroll ---------- */
    var drag = null;
    rail.addEventListener('pointerdown', function (e) {
      if (e.pointerType !== 'mouse' || e.button !== 0) return;
      if (e.target.closest('button, a')) return;
      drag = { x: e.clientX, left: rail.scrollLeft, moved: false };
      rail.classList.add('is-grabbing');
    });
    window.addEventListener('pointermove', function (e) {
      if (!drag) return;
      var dx = e.clientX - drag.x;
      if (Math.abs(dx) > 4) drag.moved = true;
      rail.scrollLeft = drag.left - dx;
    });
    window.addEventListener('pointerup', function () {
      if (!drag) return;
      rail.classList.remove('is-grabbing');
      drag = null;
    });
    rail.addEventListener('click', function (e) {
      /* swallow the click that ends a drag so links don't fire */
      if (drag && drag.moved) { e.preventDefault(); e.stopPropagation(); }
    }, true);

    /* ---------- keyboard on the rail ---------- */
    rail.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowRight') { e.preventDefault(); rail.scrollBy({ left: step(), behavior: 'smooth' }); }
      if (e.key === 'ArrowLeft') { e.preventDefault(); rail.scrollBy({ left: -step(), behavior: 'smooth' }); }
    });
  }

  function initAll() {
    document.querySelectorAll('[data-myc-vcar]').forEach(initCarousel);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initAll);
  else initAll();
  document.addEventListener('shopify:section:load', initAll);
})();
